import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Coins, 
  Gift, 
  Users, 
  Shield, 
  Gamepad2,
  ExternalLink,
  Sparkles,
  TrendingUp,
  Loader2,
  Crown,
  Star
} from "lucide-react";
import { fetchBotStats } from "../services/topggService";

const Home = () => {
  const [serverCount, setServerCount] = useState(103);
  const [totalCommands, setTotalCommands] = useState(40470);
  const [userCount, setUserCount] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Fetch live server count and command stats from APIs
  useEffect(() => {
    const loadBotStats = async () => {
      setIsLoadingStats(true);
      try {
        // Fetch bot stats (server count and commands)
        const stats = await fetchBotStats();
        setServerCount(stats.serverCount);
        setTotalCommands(stats.totalCommands);

        // Fetch bot status (user count) - using relative URL for same-port server
        try {
          const statusResponse = await fetch('/api/bot-status');
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            if (statusData.users) {
              setUserCount(statusData.users); // Use users field for user count
            } else if (statusData.guilds) {
              setUserCount(statusData.guilds); // Fallback to guilds if users not available
            }
          }
        } catch (statusError) {
          console.error('Failed to load bot status:', statusError);
        }
      } catch (error) {
        console.error('Failed to load bot stats:', error);
        // Keep fallback values
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadBotStats();
  }, []);

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const features = [
    {
      icon: Coins,
      title: "Global Economy & Casino",
      description: "Earn, gamble, invest, and climb the leaderboards with Voxel's global economy. Your bank and net worth follow you across servers.",
      image: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: Gift,
      title: "Smart Giveaways",
      description: "Run easy, spam-free giveaways with role requirements, message minimums, premium-only entries, and custom durations.",
      image: "https://images.unsplash.com/photo-1513151233558-d860c5398176",
      color: "from-pink-400 to-purple-500"
    },
    {
      icon: Users,
      title: "Custom Welcome System",
      description: "Automated welcome messages for new members to make them feel at home in your community.",
      image: "https://images.pexels.com/photos/10207025/pexels-photo-10207025.jpeg",
      color: "from-green-400 to-teal-500"
    },
    {
      icon: Shield,
      title: "Advanced Logging",
      description: "Keep track of everything happening in your server with comprehensive logging and moderation tools.",
      image: "https://images.unsplash.com/photo-1477013743164-ffc3a5e556da",
      color: "from-blue-400 to-indigo-500"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero Section */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/7267188/pexels-photo-7267188.jpeg"
            alt="Gaming Background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/90 to-blue-950/80"></div>
          
          {/* Retro Gaming Pixel Grid Overlay */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-2xl shadow-blue-500/25 pixel-border">
                  <span className="text-white font-bold text-4xl pixel-font">V</span>
                </div>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-500 bg-clip-text text-transparent">
              Level up your server
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Games, giveaways, global economy, and nonstop fun in one powerful Discord bot
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a
                href="https://discord.com/oauth2/authorize?client_id=1323444656070983701"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-blue-500/25 flex items-center gap-2 pixel-button"
              >
                <Sparkles className="w-5 h-5" />
                Add to Server
                <ExternalLink className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                {/* 8-bit shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity rounded-lg"></div>
              </a>
              
              <a
                href="https://discord.gg/CfnpERCY7r"
                target="_blank"
                rel="noopener noreferrer"
                className="group border-2 border-blue-500 text-blue-400 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-500 hover:text-white transition-all duration-300 flex items-center gap-2 pixel-button-outline relative overflow-hidden"
              >
                <Users className="w-5 h-5" />
                Join Support Server
                <ExternalLink className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                {/* Pixel scan line effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity"></div>
              </a>
            </div>

            <div className="flex items-center justify-center gap-6 text-gray-400 flex-wrap">
              <div className="flex items-center gap-2 pixel-stat">
                <TrendingUp className="w-5 h-5 text-green-400" />
                {isLoadingStats ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="font-semibold text-blue-400">Loading...</span>
                  </div>
                ) : (
                  <>
                    <span className="font-semibold text-green-400">{serverCount.toLocaleString()}</span>
                    <span>servers</span>
                  </>
                )}
              </div>
              <div className="w-2 h-2 bg-gray-600 pixel-dot"></div>
              <div className="flex items-center gap-2 pixel-stat">
                <Sparkles className="w-5 h-5 text-purple-400" />
                {isLoadingStats ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="font-semibold text-blue-400">Loading...</span>
                  </div>
                ) : (
                  <>
                    <span className="font-semibold text-purple-400">{totalCommands.toLocaleString()}</span>
                    <span>commands used</span>
                  </>
                )}
              </div>
              <div className="w-2 h-2 bg-gray-600 pixel-dot"></div>
              <div className="flex items-center gap-2 pixel-stat">
                <Users className="w-5 h-5 text-yellow-400" />
                {isLoadingStats ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="font-semibold text-blue-400">Loading...</span>
                  </div>
                ) : userCount > 0 ? (
                  <>
                    <span className="font-semibold text-yellow-400">{userCount.toLocaleString()}</span>
                    <span>users</span>
                  </>
                ) : (
                  <span>Advanced Economy</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
            >
              Powerful Features
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-400 max-w-2xl mx-auto"
            >
              Everything you need to create an engaging and fun Discord community
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
              >
                {/* Background Image */}
                <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="relative z-10">
                  <div className="flex justify-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-blue-400 transition-colors text-center">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed text-center">
                    {feature.description}
                  </p>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Community Showcase Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
            >
              Community Showcase
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-400 max-w-2xl mx-auto"
            >
              Amazing Discord servers already using Voxel to create thriving communities
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-8"
          >
            {/* HandHeldModz */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors">
                      HandHeldModz
                    </h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-400">Featured Server</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-4">
                  A thriving gaming community focused on handheld gaming modifications and discussions.
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>Active</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Coins className="w-4 h-4" />
                      <span>Economy</span>
                    </div>
                  </div>
                  <Crown className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </motion.div>

            {/* WInhanced */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                      WInhanced
                    </h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-400">Featured Server</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-4">
                  Our mission is to make gaming on Windows handhelds a more unified a console like experience!
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>Growing</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Gift className="w-4 h-4" />
                      <span>Giveaways</span>
                    </div>
                  </div>
                  <Crown className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </motion.div>

            {/* GamesInHand */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-green-400 transition-colors">
                      Games In Hand
                    </h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-400">Featured Server</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-4">
                  Active gaming community focused on handheld consoles, home of the Steam Deck Gaming YouTube channel.
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>Vibrant</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Gamepad2 className="w-4 h-4" />
                      <span>Gaming</span>
                    </div>
                  </div>
                  <Crown className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-gray-400 mb-6">
              Want to feature your server? Join our community and show us what makes your server special!
            </p>
            <a
              href="https://discord.gg/CfnpERCY7r"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
            >
              <Users className="w-5 h-5" />
              Join Community
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden retro-screen">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-purple-900/20"></div>
        
        {/* Retro Grid Background */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px'
        }}></div>
        
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 z-10">
          <div>
            {/* Retro Voxel Avatar */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-2xl shadow-blue-500/25 pixel-border">
                  <span className="text-white font-bold text-3xl pixel-font">V</span>
                </div>
                
                {/* Retro glow effect */}
                <div className="absolute -inset-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur-xl"></div>
              </div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to transform your server?
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join {serverCount.toLocaleString()} servers already using Voxel to create amazing communities
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="https://discord.com/oauth2/authorize?client_id=1323444656070983701"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-blue-500/25 flex items-center justify-center gap-2 pixel-button"
              >
                <Sparkles className="w-5 h-5" />
                Get Started Now
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* 8-bit Status Bar Style */}
            <div className="flex justify-center items-center gap-1 bg-slate-900/50 px-6 py-3 rounded-lg border border-blue-500/20 max-w-md mx-auto">
              <div className="w-2 h-2 bg-green-400 rounded-sm"></div>
              <span className="text-green-400 font-mono text-sm">ONLINE</span>
              <div className="w-1 h-4 bg-blue-400 mx-2"></div>
              <div className="w-2 h-2 bg-blue-400 rounded-sm"></div>
              <span className="text-blue-400 font-mono text-sm">{serverCount}</span>
              <div className="w-1 h-4 bg-purple-400 mx-2"></div>
              <div className="w-2 h-2 bg-purple-400 rounded-sm"></div>
              <span className="text-purple-400 font-mono text-sm">{Math.floor(totalCommands / 1000)}K</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;